import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import {
  CircularProgress,
  IconButton,
  InputBase,
  Paper,
  Typography,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

import { ASSISTANT_NAME } from '~/components/otto/branding';
import { useOtto } from '~/components/otto/OttoProvider';
import { SURFACE_RADIUS } from '~/theme';

export function OttoComposer() {
  const theme = useTheme();
  const { state, setDraft, submitDraft, stopActive } = useOtto();
  const id = state.activeSessionId;
  const session = id ? state.sessions[id] : null;
  const draft = session?.draft ?? '';
  const status = session?.status ?? 'ready';
  const isBusy = status === 'submitted' || status === 'streaming';
  const hasDraft = draft.trim().length > 0;

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        maxWidth: 860,
        borderRadius: `${SURFACE_RADIUS}px`,
        border: `1.5px solid ${alpha(theme.palette.primary.main, 0.2)}`,
        background: '#FFFFFF',
        boxShadow: `0 2px 12px ${alpha(theme.palette.primary.main, 0.08)}`,
        transition: 'border-color 0.2s, box-shadow 0.2s',
        '&:focus-within': {
          borderColor: alpha(theme.palette.primary.main, 0.5),
          boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.14)}`,
        },
        display: 'flex',
        alignItems: 'flex-end',
        px: 2,
        py: 1,
      }}
    >
      <InputBase
        multiline
        maxRows={6}
        placeholder={
          isBusy
            ? `${ASSISTANT_NAME} is replying — please wait…`
            : `Ask ${ASSISTANT_NAME} anything about your portfolio...`
        }
        value={draft}
        disabled={!id || isBusy}
        onChange={(e) => id && setDraft(id, e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey && hasDraft && !isBusy) {
            e.preventDefault();
            submitDraft();
          }
        }}
        sx={{
          flex: 1,
          fontSize: '1rem',
          py: 0.5,
          '& .MuiInputBase-input::placeholder': { color: alpha('#000', 0.4) },
        }}
        inputProps={{ 'aria-label': `Ask ${ASSISTANT_NAME}` }}
      />
      {isBusy ? (
        <>
          <Typography
            variant="caption"
            sx={{
              color: 'text.secondary',
              mb: 1,
              mr: 1,
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'center',
              gap: 0.5,
            }}
          >
            <CircularProgress size={10} thickness={6} />
            {status === 'streaming' ? 'Replying' : 'Thinking'}
          </Typography>
          <IconButton
            size="small"
            onClick={() => stopActive()}
            aria-label={`Stop ${ASSISTANT_NAME}`}
            sx={{
              mb: 0.25,
              background: alpha(theme.palette.error.main, 0.12),
              color: theme.palette.error.main,
              width: 32,
              height: 32,
              '&:hover': { background: alpha(theme.palette.error.main, 0.2) },
            }}
          >
            <StopRoundedIcon fontSize="small" />
          </IconButton>
        </>
      ) : (
        <IconButton
          size="small"
          disabled={!hasDraft}
          onClick={() => submitDraft()}
          aria-label="Send message"
          sx={{
            mb: 0.25,
            background: hasDraft
              ? `linear-gradient(135deg, ${theme.palette.primary.main}, #4A90D9)`
              : alpha('#000', 0.08),
            color: hasDraft ? '#fff' : alpha('#000', 0.3),
            width: 32,
            height: 32,
            '&:hover': {
              background: hasDraft
                ? `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`
                : undefined,
            },
          }}
        >
          <ArrowUpwardRoundedIcon fontSize="small" />
        </IconButton>
      )}
    </Paper>
  );
}
